import React from "react";
import "@/i18n";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useDocumentTitle } from "./lib/useDocumentTitle";
import { AuthProvider } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { RoleGate } from "@/components/RoleGate";
import { ComingSoon } from "@/components/ComingSoon";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AcceptInvite from "@/pages/AcceptInvite";
import Dashboard from "@/pages/Dashboard";
import TenantSettings from "@/pages/TenantSettings";
import UsersSettings from "@/pages/UsersSettings";
import Profile from "@/pages/Profile";
import LlmSettings from "@/pages/LlmSettings";
import IntegrationsSettings from "@/pages/IntegrationsSettings";
import CreditsSettings from "@/pages/CreditsSettings";
import Brands from "@/pages/Brands";
import BrandDetail from "@/pages/BrandDetail";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Competitors from "@/pages/Competitors";
import AutofillBrandReview from "@/pages/AutofillBrandReview";
import AutofillProductReview from "@/pages/AutofillProductReview";
import Projects from "@/pages/Projects";
import ProjectNew from "@/pages/ProjectNew";
import ProjectDetail from "@/pages/ProjectDetail";
import RunDetail from "@/pages/RunDetail";
import Evidence from "@/pages/Evidence";
import Findings from "@/pages/Findings";
import FindingDetail from "@/pages/FindingDetail";
import Reports from "@/pages/Reports";
import ReportDetail from "@/pages/ReportDetail";
import PublicReport from "@/pages/PublicReport";
import Landing from "@/pages/Landing";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";

const ADMIN_ROLES = ["owner", "admin"];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <DocumentTitleEffect />
        <Routes>
          {/* Marketing landing — public. Landing itself redirects to
              /dashboard when the user is already signed in. */}
          <Route path="/" element={<Landing />} />
          <Route path="/legal/privacy" element={<Legal variant="privacy" />} />
          <Route path="/legal/terms" element={<Legal variant="terms" />} />

          {/* Public auth routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/share/r/:token" element={<PublicReport />} />

          {/* Protected app shell */}
          <Route element={<AppShell />}>
            <Route path="/dashboard" element={<Dashboard />} />

            {/* Phase 2 — Brands / Products / Competitors */}
            <Route path="/brands" element={<Brands />} />
            <Route path="/brands/new/review" element={<AutofillBrandReview />} />
            <Route path="/brands/:id/products/new/review" element={<AutofillProductReview />} />
            <Route path="/brands/:id" element={<BrandDetail />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/competitors" element={<Competitors />} />

            {/* Phase 3 — Research projects + run pipeline */}
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/new" element={<ProjectNew />} />
            <Route path="/projects/:id" element={<ProjectDetail />} />
            <Route path="/projects/:id/runs/:runId" element={<RunDetail />} />

            {/* Phase 4 — Evidence library + Findings cross-project */}
            <Route path="/evidence" element={<Evidence />} />
            <Route path="/findings" element={<Findings />} />
            <Route path="/findings/:id" element={<FindingDetail />} />

            {/* Future-phase stubs */}
            <Route path="/reports" element={<Reports />} />
            <Route path="/reports/:id" element={<ReportDetail />} />

            {/* Settings (role-gated) */}
            <Route
              path="/settings"
              element={<Navigate to="/settings/tenant" replace />}
            />
            <Route
              path="/settings/tenant"
              element={
                <RoleGate roles={ADMIN_ROLES}>
                  <TenantSettings />
                </RoleGate>
              }
            />
            <Route
              path="/settings/users"
              element={
                <RoleGate roles={ADMIN_ROLES}>
                  <UsersSettings />
                </RoleGate>
              }
            />
            <Route path="/settings/profile" element={<Profile />} />
            <Route
              path="/settings/llm"
              element={
                <RoleGate roles={ADMIN_ROLES}>
                  <LlmSettings />
                </RoleGate>
              }
            />
            <Route
              path="/settings/integrations"
              element={
                <RoleGate roles={ADMIN_ROLES}>
                  <IntegrationsSettings />
                </RoleGate>
              }
            />
            <Route
              path="/settings/credits"
              element={<CreditsSettings />}
            />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;

// Tiny helper — has to live inside <BrowserRouter> so ``useLocation`` works.
function DocumentTitleEffect() {
  useDocumentTitle();
  return null;
}
